import React from 'react';

/**
 * Sdílené typy menu + filtr viditelnosti (HO-88).
 * Jediný zdroj pravdy pro AdminLayout (desktop sidebar — top-level i children)
 * a MobileBottomNav (drawer „Více") — dříve měl každý vlastní kopii typu i filtru.
 *
 * featureFlag (klíč MODUL_* / FEATURE_*) gateuje POUZE viditelnost UI (D1);
 * bezpečnostní hranicí zůstávají role na BE (D2). Fail-closed: neznámý/nenačtený
 * flag => položka skrytá.
 */
export interface NavItem {
    label: string;
    path: string;
    icon: React.ReactNode;
    adminOnly?: boolean;
    /** Klíč feature flagu — položka viditelná jen při flag=true (fail-closed). */
    featureFlag?: string;
    children?: NavItem[];
}

export interface NavGroup {
    label?: string;
    items: NavItem[];
}

export type IsFeatureEnabled = (key: string) => boolean;

/**
 * Viditelnost jedné položky: adminOnly + featureFlag musí platit zároveň.
 * Parent bez vlastní path s 0 viditelnými dětmi se skrývá (jinak by v menu
 * zůstala rozbalitelná položka bez obsahu).
 */
export const isNavItemVisible = (
    item: NavItem,
    isAdmin: boolean,
    isFeatureEnabled: IsFeatureEnabled
): boolean => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.featureFlag && !isFeatureEnabled(item.featureFlag)) return false;
    if (item.children && !item.path) {
        return item.children.some((child) => isNavItemVisible(child, isAdmin, isFeatureEnabled));
    }
    return true;
};

/**
 * Vrátí viditelné položky; u položek s children vrací kopii s už přefiltrovanými
 * dětmi (render pak děti jen mapuje, bez dalšího filtru). Nemutuje vstup.
 */
export const filterNavItems = (
    items: NavItem[],
    isAdmin: boolean,
    isFeatureEnabled: IsFeatureEnabled
): NavItem[] =>
    items
        .filter((item) => isNavItemVisible(item, isAdmin, isFeatureEnabled))
        .map((item) => {
            if (!item.children) return item;
            const children = item.children.filter((child) =>
                isNavItemVisible(child, isAdmin, isFeatureEnabled)
            );
            return { ...item, children: children.length > 0 ? children : undefined };
        });
